import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { currencyRatesService } from "@/services/system-admin/admin-data.service";
import {
  CurrencyRate,
  CurrencyRateHistory,
  UpdateCurrencyRatePayload,
} from "@/types/admin/currencyRates";

interface CurrencyRatesState {
  current: CurrencyRate | null;
  history: CurrencyRateHistory[];
  loading: boolean;
  updateLoading: boolean;
  error: string | null;
}

const initialState: CurrencyRatesState = {
  current: null,
  history: [],
  loading: false,
  updateLoading: false,
  error: null,
};

export const fetchCurrentRate = createAsyncThunk(
  "currencyRates/fetchCurrent",
  async (_, { rejectWithValue }) => {
    try {
      return await currencyRatesService.getCurrentRate();
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch rate");
    }
  }
);

export const fetchRateHistory = createAsyncThunk(
  "currencyRates/fetchHistory",
  async (_, { rejectWithValue }) => {
    try {
      return await currencyRatesService.getRateHistory();
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch history");
    }
  }
);

export const updateRate = createAsyncThunk(
  "currencyRates/update",
  async (payload: UpdateCurrencyRatePayload, { rejectWithValue }) => {
    try {
      return await currencyRatesService.updateRate(payload);
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to update rate");
    }
  }
);

const currencyRatesSlice = createSlice({
  name: "currencyRates",
  initialState,
  reducers: {
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentRate.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchCurrentRate.fulfilled, (state, action) => { state.loading = false; state.current = action.payload; })
      .addCase(fetchCurrentRate.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })

      .addCase(fetchRateHistory.pending, (state) => { state.loading = true; })
      .addCase(fetchRateHistory.fulfilled, (state, action) => { state.loading = false; state.history = action.payload; })
      .addCase(fetchRateHistory.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })

      .addCase(updateRate.pending, (state) => { state.updateLoading = true; state.error = null; })
      .addCase(updateRate.fulfilled, (state, action) => { state.updateLoading = false; state.current = action.payload; })
      .addCase(updateRate.rejected, (state, action) => { state.updateLoading = false; state.error = action.payload as string; });
  },
});

export const { clearError } = currencyRatesSlice.actions;
export default currencyRatesSlice.reducer;